import { JSX, ReactNode, forwardRef } from 'react';
import cn from 'classnames';

import { generateResponsiveClasses, TResponsiveValue } from '@creditflowresearch/uilib/shared/responsive-classes';
import { processInlineStyles } from '@creditflowresearch/uilib/shared/style-processors';

import styles from './flex.module.scss';

const transformFlexValue = (value: string): string => {
  return value
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
};

export type TFlexDirection = 'row' | 'column' | 'row-reverse' | 'column-reverse';
export type TFlexWrap = 'nowrap' | 'wrap' | 'wrap-reverse';
export type TAlignItems = 'flex-start' | 'flex-end' | 'center' | 'baseline' | 'stretch';
export type TJustifyContent = 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly';
export type TAlignContent = 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'stretch';
export type TAlignSelf = 'auto' | 'flex-start' | 'flex-end' | 'center' | 'baseline' | 'stretch';
export type TFlexBasis = 'auto' | '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | '11' | '12';
export type TFlexGrow = '0' | '1' | '2' | '3' | '4' | '5';
export type TFlexShrink = '0' | '1' | '2' | '3' | '4' | '5';
export type TFlexShorthand = '1' | '0' | 'auto' | 'none' | 'initial';

export type { TResponsiveValue };

export interface IFlexProps {
  children?: ReactNode,
  className?: string,
  direction?: TResponsiveValue<TFlexDirection>,
  wrap?: TResponsiveValue<TFlexWrap>,
  align?: TResponsiveValue<TAlignItems>,
  justify?: TResponsiveValue<TJustifyContent>,
  alignContent?: TResponsiveValue<TAlignContent>,
  alignSelf?: TResponsiveValue<TAlignSelf>,
  basis?: TResponsiveValue<TFlexBasis>,
  grow?: TResponsiveValue<TFlexGrow>,
  shrink?: TResponsiveValue<TFlexShrink>,
  flex?: TResponsiveValue<TFlexShorthand>,
  gap?: TResponsiveValue<number> | TResponsiveValue<string>,
  dataTest?: string,
  as?: keyof JSX.IntrinsicElements,
  style?: React.CSSProperties,
  onClick?: (event: React.MouseEvent<HTMLElement>) => void,
}

const Flex = forwardRef<HTMLDivElement, IFlexProps>(({
  children,
  className,
  direction = 'row',
  wrap = 'nowrap',
  align = 'stretch',
  justify = 'flex-start',
  alignContent,
  alignSelf,
  basis,
  grow,
  shrink,
  flex,
  gap,
  dataTest = 'flex',
  as: Component = 'div' as any,
  style,
  ...props
}, ref) => {
  const flexClasses = cn(
    styles.flex,
    generateResponsiveClasses(direction, 'direction', styles, transformFlexValue),
    generateResponsiveClasses(wrap, 'wrap', styles, transformFlexValue),
    generateResponsiveClasses(align, 'align', styles, transformFlexValue),
    generateResponsiveClasses(justify, 'justify', styles, transformFlexValue),
    generateResponsiveClasses(alignContent, 'alignContent', styles, transformFlexValue),
    generateResponsiveClasses(alignSelf, 'alignSelf', styles, transformFlexValue),
    generateResponsiveClasses(basis, 'basis', styles),
    generateResponsiveClasses(grow, 'grow', styles),
    generateResponsiveClasses(shrink, 'shrink', styles),
    generateResponsiveClasses(flex, 'flex', styles),
    generateResponsiveClasses(gap, 'gap', styles),
    className,
  );

  const inlineStyles = processInlineStyles({ gap }, styles);

  const combinedStyles = { ...inlineStyles, ...style };

  return (
    <Component
      ref={ ref }
      className={ flexClasses }
      data-test={ dataTest }
      style={ combinedStyles }
      { ...props }
    >
      { children }
    </Component>
  );
});

export default Flex;
